import React, { type ReactNode } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Link } from 'react-router';
import { ArrowRight } from 'lucide-react';
import { Card, CardMeta, CardTitle } from '../../../shared/ui/Card';
import { useI18n } from '../../../i18n/I18nProvider';
import { readRecentPages } from '../../../app/layout/recentPages';
import './ErrorLayout.css';
import { RiseMark } from '../../../shared/ui/Triangles';
import type { Photo } from '../../../shared/ui/photos/photos';

interface ErrorLayoutProps {
  /** "404", "403", "500": drawn huge behind the text, in Cool Grey Light. Plain type, never triangles. */
  code: string;
  /** "Error 404": the 12/500 eyebrow. */
  eyebrow: string;
  /** The first clause of the sentence, in the primary colour. */
  title: string;
  /** The rest of the sentence, in muted. */
  continuation: ReactNode;
  /** Primary first, then quieter ones (spec 4.11). */
  actions: ReactNode;
  /** Anything under the actions: the error code chip, the recent pages. */
  children?: ReactNode;
  /**
   * A calm photo on the right edge, fading into the page toward the text (the 404 and "Could not
   * reach Grow"). The code then sits over it, quieter. Decorative.
   */
  photo?: Photo;
}

/**
 * The 404, 403 and 500 (spec 4.11, after 404s.design's Kobbe, Premotio and UPCLOSE):
 * inside the shell, on the left edge, one two-tone sentence at display size, the actions
 * in falling weight, the code as giant quiet type behind it. The 404 opens with the Rise
 * whose big triangle is missing (an outline only): "This page didn't grow here". No
 * corner framers: those are Visma's gesture. This is the page's one brand moment.
 */
export default function ErrorLayout({ code, eyebrow, title, continuation, actions, children, photo }: ErrorLayoutProps) {
  const reduce = useReducedMotion();
  return (
    <section className={`errp${photo ? ' errp--photo' : ''}`} aria-labelledby="errp-title">
      {photo && (
        <img
          className="errp__photo"
          src={photo.src}
          width={photo.width}
          height={photo.height}
          style={{ objectPosition: photo.position }}
          alt=""
          aria-hidden="true"
          loading="eager"
          decoding="async"
          data-testid="errp-photo"
        />
      )}
      <span className="errp__code" aria-hidden="true">{code}</span>
      <div className="errp__body">
        {code === '404' && <RiseMark variant="missing" size={48} className="errp__rise" />}
        <p className="errp__eyebrow">{eyebrow}</p>
        <motion.h1
          id="errp-title"
          className="errp__title"
          initial={reduce ? false : { opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.24, ease: [0.16, 1, 0.3, 1] }}
        >
          {title} <span className="errp__muted">{continuation}</span>
        </motion.h1>
        <div className="errp__actions">{actions}</div>
        {children}
      </div>
    </section>
  );
}

/**
 * "Pages you opened recently": three small cards from this tab's history, so the way
 * back is one click (the Premotio idea). Nothing at all when there is no history.
 */
export function RecentPages({ exclude }: { exclude?: string }) {
  const { t } = useI18n();
  const pages = readRecentPages().filter((p) => p.path !== exclude).slice(0, 3);
  if (pages.length === 0) return null;
  return (
    <nav className="errp__recent" aria-labelledby="errp-recent-title">
      <h2 id="errp-recent-title" className="errp__recent-title">{t('errorPages.recentTitle')}</h2>
      <ul className="errp__recent-list">
        {pages.map((p) => (
          <li key={p.path}>
            <Card as={Link} to={p.path} interactive className="errp__recent-card">
              <CardTitle>{p.title}</CardTitle>
              <CardMeta>
                <span className="errp__recent-path">{p.path}</span>
                <ArrowRight size={14} aria-hidden="true" className="errp__recent-arrow" />
              </CardMeta>
            </Card>
          </li>
        ))}
      </ul>
    </nav>
  );
}
